<template>
  <div class="order-details">
    <v-card class="card">
      <h2 class="order-summary__title">
        {{ $t("message.payments.paymentSummary") }}
      </h2>
      <div v-if="!paymentPending" class="payment-status">
        <div>
          <div class="payment-status__header">
            <v-icon :class="statusClasses[statusCode]">{{
              icons[statusCode]
            }}</v-icon>
            <div>{{ status }}</div>
          </div>
          <p
            v-if="statusCode === 'succeeded'"
            v-html="$t('message.payments.paymentSuccessDescription')"
          ></p>
          <p
            v-if="statusCode === 'requires_payment_method'"
            v-html="$t('message.payments.paymentErrorDescription')"
          ></p>
        </div>
      </div>
      <div v-if="paymentPending" class="order-details__processing">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
        <div>{{ $t("message.payments.processingPayment") }}</div>
      </div>
      <template v-if="statusCode === 'succeeded' && !paymentPending">
        <ui-button
          class="order-details__go-next"
          color="primary"
          @click="handleGoToGmbAccounts"
        >
          {{ $t("message.payments.startUsingCenterAi") }}
        </ui-button>
      </template>

      <ui-button
        v-if="statusCode === 'requires_payment_method' && !paymentPending"
        class="order-details__go-back"
        color="primary"
        @click="$router.push({ name: 'order-details' })"
      >
        {{ $t("message.common.tryAgain") }}
      </ui-button>
    </v-card>
  </div>
</template>

<script>
import { loadStripeScript } from "@/helpers/load-stripe-script";
import serviceHandler from "@/helpers/handlers/service";
import PaymentsService from "@/services/payments";
import usersService from "@/services/users";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

export default {
  name: "Completed",
  data() {
    return {
      intervalId: null,
      status: null,
      statusCode: null,
      statusClasses: {
        succeeded: "successful",
        processing: "pending",
        requires_payment_method: "failed"
      },
      icons: {
        succeeded: "icon-status-success",
        processing: "icon-status-warning",
        requires_payment_method: "icon-status-error"
      },
      paymentPending: false
    };
  },
  computed: {
    isBillingPending() {
      return (
        this.$store.state.company.company.initial_payment_status === "pending"
      );
    },
    hasBillingSubscription() {
      return this.$store.state.company.company.has_billing_subscription;
    }
  },
  created() {
    this.paymentPending = true;
  },
  async mounted() {
    await loadStripeScript();
    await this.checkStatus();
    await this.refreshCompanyInfo();
  },
  methods: {
    updateIntercom(time) {
      setTimeout(() => {
        if ("Intercom" in window) {
          window.Intercom("update");
        }
      }, time);
    },

    handleGoToGmbAccounts() {
      this.updateIntercom(5000);
      this.updateIntercom(300000);

      this.$router.push({ name: "profile-gmb-accounts" });
    },
    async postAfterPayment(intent) {
      const data = await serviceHandler(
        PaymentsService.postAfterPayment({ payment_intent: intent })
      );
    },
    async setPaymentDetails(intent) {
      if (!intent) {
        this.setErrorState();
        return;
      }
      const clientIntent = new URLSearchParams(window.location.search).get(
        "payment_intent"
      );
      this.statusCode = intent.status;

      switch (intent.status) {
        case "succeeded":
          this.status = this.$t("message.payments.status.success");
          break;
        case "processing":
          this.status = this.$t("message.payments.status.pending");
          break;
        case "requires_payment_method":
          this.status = this.$t("message.payments.status.failed");
          break;
        default:
          break;
      }
      await this.postAfterPayment(clientIntent);
    },
    async checkStatus() {
      const stripe = window.Stripe(process.env.VUE_APP_STRIPE_PK);

      const clientSecret = new URLSearchParams(window.location.search).get(
        "payment_intent_client_secret"
      );

      if (!clientSecret) {
        this.setErrorState();
        return;
      }

      const { paymentIntent } = await stripe.retrievePaymentIntent(
        clientSecret
      );

      await this.setPaymentDetails(paymentIntent);
    },
    async refreshCompanyInfo() {
      this.intervalId = setInterval(async () => {
        if (this.statusCode === "requires_action") {
          await this.checkStatus();
        } else {
          await this.$store.dispatch("company/fetchCompanies");
          if (
            (!this.isBillingPending && this.hasBillingSubscription) ||
            this.statusCode === "requires_payment_method"
          ) {
            if (
              ["succeeded", "requires_payment_method"].includes(this.statusCode)
            ) {
              this.$gtm.trackCustomEvent({
                event: GTM_EVENT_NAME.PAYMENT_FINISHED,
                payment_status:
                  this.statusCode === "succeeded" ? "success" : "failure"
              });
            }
            const { company_subscription } = await serviceHandler(
              usersService.getUserDetails()
            );
            this.$store.dispatch(
              "auth/setPlanRestrictions",
              company_subscription
            );

            this.paymentPending = false;
            clearInterval(this.intervalId);
            return;
          }
        }
      }, 1000);
    },
    setErrorState() {
      this.$notify({
        group: "default",
        type: "error",
        text: this.$t("message.payments.error")
      });
    }
  },
  destroyed() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
};
</script>

<style lang="scss" scoped>
.card {
  padding: 3 * $spacing 4 * $spacing;
  display: flex;
  flex-direction: column;
  gap: $spacing * 3;
  height: auto;
  color: $dark-blue-3;
}
.header {
  display: flex;
  align-items: center;
  gap: $spacing * 2.5;

  &__title {
    @include font-size(30px);
  }
}

.order-summary {
  background: rgba($gray-300, 0.2);
  padding: $spacing * 2.5;
  display: flex;
  gap: $spacing * 3;
  flex-direction: column;
  border-radius: 7px !important;

  &__title {
    @include font-size(24px);
  }

  &__total-price {
    padding: $spacing * 3 0 $spacing * 0.5 0;
    border-top: 1px solid $gray-300;
    display: flex;
    justify-content: space-between;
    align-items: center;
    @include font-size(20px);

    * {
      line-height: 1em;
    }

    span {
      @include font-size(14px);
    }
  }
}

.order-details {
  &__go-next,
  &__go-back {
    margin-top: $spacing * 3;
  }

  &__go-next {
    align-self: flex-end;
  }

  &__go-back {
    align-self: flex-end;
  }

  &__processing {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing * 2.5;
  }
}

.order-summary,
.order-checkout {
  width: 100%;
}

.payment-status {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;

  &__header {
    display: flex;
    align-items: center;
    gap: $spacing * 0.5;
    margin-bottom: $spacing * 2;
    font-weight: bold;
  }
  p {
    @include font-size(14px);
    margin: 0;
  }

  span {
    font-weight: 600;
  }
}
::v-deep {
  .v-icon.successful {
    color: $success !important;
  }
  .v-icon.pending {
    color: $orange !important;
  }
  .v-icon.failed {
    color: $danger !important;
  }
}
</style>
